import type mongoose from "mongoose"

import { GLOBAL_DB_SUFFIX } from "./dbNames"
import { RBRtsChangeSchema, type IRBRtsChange } from "./models/RBRtsChange"
import { RBRtsCounterSchema, type IRBRtsCounter } from "./models/RBRtsCounter"


type RtsChangeOp = IRBRtsChange["op"]

const RTS_COUNTER_ID = "rts"
const EXCLUDED_MODEL_NAMES = new Set(["RBRtsChange", "RBRtsCounter"])

const maxDeleteIdsRaw = process.env.RB_RTS_DELETE_LOG_MAX_IDS ?? ""
const maxDeleteIds = Number.isFinite(Number(maxDeleteIdsRaw)) ? Math.max(1, Math.floor(Number(maxDeleteIdsRaw))) : 5000

type DeleteMeta = {
  modelName: string
  ids: string[]
  reset: boolean
  session?: mongoose.ClientSession
}

const deleteMetaByQuery = new WeakMap<object, DeleteMeta>()

const hasToString = (value: unknown): value is { toString: () => string } => {
  if (typeof value !== "object" || value === null) return false
  const maybe = value as { toString?: unknown }
  return typeof maybe.toString === "function"
}

const normalizeId = (id: unknown): string | null => {
  if (!id) return null
  if (typeof id === "string") return id
  if (hasToString(id)) return id.toString()
  return null
}

const getDbName = (db: unknown): string => {
  if (!db || typeof db !== "object") return ""
  const maybe = db as { name?: unknown; db?: { databaseName?: unknown } }
  const raw = maybe.name ?? maybe.db?.databaseName
  return typeof raw === "string" ? raw : ""
}

const isGlobalDb = (db: unknown): boolean => getDbName(db).endsWith(GLOBAL_DB_SUFFIX)

type RtsCounterDoc = IRBRtsCounter
type RtsChangeDoc = IRBRtsChange

const getQuerySession = (query: MongooseQuery): mongoose.ClientSession | undefined => {
  const opts = typeof query.getOptions === "function" ? query.getOptions() : undefined
  if (!opts || typeof opts !== "object") return undefined
  const session = (opts as { session?: unknown }).session
  if (!session || typeof session !== "object") return undefined
  return session as mongoose.ClientSession
}

const getRtsModels = (db: mongoose.Connection) => {
  const RtsCounter = (db.models.RBRtsCounter as mongoose.Model<RtsCounterDoc>)
    ?? db.model<RtsCounterDoc>("RBRtsCounter", RBRtsCounterSchema)
  const RtsChange = (db.models.RBRtsChange as mongoose.Model<RtsChangeDoc>)
    ?? db.model<RtsChangeDoc>("RBRtsChange", RBRtsChangeSchema)
  return { RtsCounter, RtsChange }
}

const allocateSeqRange = async (
  db: mongoose.Connection,
  count: number,
  session?: mongoose.ClientSession,
): Promise<{ start: number; end: number }> => {
  const { RtsCounter } = getRtsModels(db)
  const updated = await RtsCounter.findOneAndUpdate(
    { _id: RTS_COUNTER_ID },
    { $inc: { seq: count } },
    { upsert: true, returnDocument: "after", setDefaultsOnInsert: true, projection: { seq: 1 }, session },
  ).lean()

  const end = Number(updated?.seq ?? 0)
  const start = end - count + 1
  return { start, end }
}

const insertChanges = async (
  db: mongoose.Connection,
  changes: Array<{ seq: number; modelName: string; op: RtsChangeOp; docId?: string | null }>,
  session?: mongoose.ClientSession,
): Promise<void> => {
  if (!changes.length) return
  const { RtsChange } = getRtsModels(db)
  const ts = new Date()
  const docs = changes.map((c) => ({
    seq: c.seq,
    modelName: c.modelName,
    op: c.op,
    docId: c.docId ?? undefined,
    ts,
  }))

  if (session) {
    await RtsChange.insertMany(docs, { session })
    return
  }

  await RtsChange.insertMany(docs)
}

const recordDeleteChanges = async (
  db: mongoose.Connection,
  modelName: string,
  ids: string[],
  session?: mongoose.ClientSession,
): Promise<void> => {
  const uniqueIds = Array.from(new Set(ids)).filter(Boolean)
  if (!uniqueIds.length) return
  const { start } = await allocateSeqRange(db, uniqueIds.length, session)
  await insertChanges(db, uniqueIds.map((docId, idx) => ({
    seq: start + idx,
    modelName,
    op: "delete",
    docId,
  })), session)
}

const recordResetModel = async (db: mongoose.Connection, modelName: string, session?: mongoose.ClientSession): Promise<void> => {
  const { start } = await allocateSeqRange(db, 1, session)
  await insertChanges(db, [{ seq: start, modelName, op: "reset_model" }], session)
}

type MongooseQuery = mongoose.Query<unknown, unknown> & {
  model?: {
    modelName?: string
    db?: unknown
    find: (filter: unknown, projection: unknown) => unknown
  }
  getFilter?: () => unknown
  getQuery?: () => unknown
  getOptions?: () => unknown
}

const captureDeleteMeta = async (query: MongooseQuery, mode: "one" | "many") => {
  const modelName = String(query?.model?.modelName ?? "")
  if (!modelName || modelName.startsWith("RB") || EXCLUDED_MODEL_NAMES.has(modelName)) return
  if (isGlobalDb(query?.model?.db)) return

  const filter = typeof query.getFilter === "function" ? query.getFilter() : query.getQuery?.() ?? {}
  const session = getQuerySession(query)
  const findQuery = query.model.find(filter, { _id: 1 }) as unknown as {
    session?: (s: mongoose.ClientSession) => unknown
    limit: (n: number) => unknown
    lean: () => unknown
  }
  if (session && typeof findQuery.session === "function") {
    findQuery.session(session)
  }

  findQuery.lean()

  if (mode === "one") {
    findQuery.limit(1)
  } else {
    findQuery.limit(maxDeleteIds + 1)
  }

  const docs = await findQuery
  const ids = Array.isArray(docs)
    ? docs
      .map((d) => normalizeId((d as { _id?: unknown })?._id))
      .filter((id): id is string => Boolean(id))
    : []
  const reset = mode === "many" && ids.length > maxDeleteIds
  const trimmedIds = reset ? [] : ids

  const meta: DeleteMeta = {
    modelName,
    ids: trimmedIds,
    reset,
    session,
  }

  deleteMetaByQuery.set(query, meta)
}

const flushDeleteMeta = async (query: MongooseQuery) => {
  const meta = deleteMetaByQuery.get(query)
  deleteMetaByQuery.delete(query)
  if (!meta) return

  const db = query?.model?.db as mongoose.Connection | undefined
  if (!db) return

  try {
    if (meta.reset) {
      await recordResetModel(db, meta.modelName, meta.session)
    } else {
      await recordDeleteChanges(db, meta.modelName, meta.ids, meta.session)
    }
  } catch {
    return
  }
}

export const rtsChangeLogPlugin = (schema: mongoose.Schema): void => {
  schema.pre("deleteOne", { query: true, document: false }, async function(this: MongooseQuery) {
    await captureDeleteMeta(this, "one")
  })

  schema.pre("deleteMany", { query: true, document: false }, async function(this: MongooseQuery) {
    await captureDeleteMeta(this, "many")
  })

  schema.post("deleteOne", { query: true, document: false }, async function(this: MongooseQuery) {
    await flushDeleteMeta(this)
  })

  schema.post("deleteMany", { query: true, document: false }, async function(this: MongooseQuery) {
    await flushDeleteMeta(this)
  })

  schema.post("findOneAndDelete", { query: true, document: false }, async function(this: MongooseQuery, doc: unknown) {
    const modelName = String(this?.model?.modelName ?? "")
    if (!modelName || modelName.startsWith("RB") || EXCLUDED_MODEL_NAMES.has(modelName)) return

    const db = this?.model?.db as mongoose.Connection | undefined
    if (!db) return
    if (isGlobalDb(db)) return

    const docId = normalizeId((doc as { _id?: unknown } | null | undefined)?._id)
    if (!docId) return

    try {
      const session = getQuerySession(this)
      await recordDeleteChanges(db, modelName, [docId], session)
    } catch {
      return
    }
  })
}
